import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { Kicker } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Shell({ title, lead, children, testId }) {
  const { t, lang } = useI18n();
  return (
    <div data-testid={testId} className="cs-container flex min-h-[80vh] items-center py-16">
      <div className="mx-auto w-full max-w-md">
        <Link to={`/${lang}`} className="font-display text-lg font-semibold tracking-tight">
          CONSULTANTS SPARKS
        </Link>
        <Kicker className="mt-8">{t("footer.tagline")}</Kicker>
        <h1 className="mt-4 font-display text-3xl font-light sm:text-4xl">{title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{lead}</p>
        <div className="mt-8 border border-border bg-surface p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}

export function Login() {
  const { t, lang } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(form.email, form.password);
      navigate(STAFF_ROLES.includes(user.role) ? `/${lang}/admin` : `/${lang}/portal`);
    } catch (err) {
      setError(apiError(err, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title={t("auth.loginTitle")} description={t("auth.loginLead")} path="/login" noindex />
      <Shell testId="login-page" title={t("auth.loginTitle")} lead={t("auth.loginLead")}>
        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <Label htmlFor="login-email">{t("auth.email")}</Label>
            <Input id="login-email" data-testid="login-email" type="email" required value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="login-password">{t("auth.password")}</Label>
            <Input id="login-password" data-testid="login-password" type="password" required value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2" />
          </div>
          {error && (
            <p data-testid="login-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
            {busy ? t("common.loading") : t("cta.login")}
          </Button>
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <Link to={`/${lang}/forgot-password`} data-testid="login-forgot-link" className="hover:text-accent">
              {t("auth.forgotLink")}
            </Link>
            <span>
              {t("auth.noAccount")}{" "}
              <Link to={`/${lang}/register`} data-testid="login-register-link" className="text-accent hover:underline">
                {t("cta.register")}
              </Link>
            </span>
          </div>
        </form>
      </Shell>
    </>
  );
}

export function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register({ ...form, preferred_language: lang });
      navigate(`/${lang}/portal`);
    } catch (err) {
      setError(apiError(err, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title={t("auth.registerTitle")} description={t("auth.registerLead")} path="/register" noindex />
      <Shell testId="register-page" title={t("auth.registerTitle")} lead={t("auth.registerLead")}>
        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="reg-first">{t("auth.firstName")}</Label>
              <Input id="reg-first" data-testid="register-first-name" required value={form.first_name}
                     onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="reg-last">{t("auth.lastName")}</Label>
              <Input id="reg-last" data-testid="register-last-name" required value={form.last_name}
                     onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-2" />
            </div>
          </div>
          <div>
            <Label htmlFor="reg-email">{t("auth.email")}</Label>
            <Input id="reg-email" data-testid="register-email" type="email" required value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="reg-password">{t("auth.password")}</Label>
            <Input id="reg-password" data-testid="register-password" type="password" minLength={8} required value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2" />
            <p className="mt-1 text-xs text-muted-foreground">{t("auth.passwordRule")}</p>
          </div>
          {error && (
            <p data-testid="register-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
            {busy ? t("common.loading") : t("cta.register")}
          </Button>
          <p className="text-sm text-muted-foreground">
            {t("auth.hasAccount")}{" "}
            <Link to={`/${lang}/login`} data-testid="register-login-link" className="text-accent hover:underline">
              {t("cta.login")}
            </Link>
          </p>
        </form>
      </Shell>
    </>
  );
}

export function ForgotPassword() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, preferred_language: lang });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title={t("auth.forgotTitle")} description={t("auth.forgotLead")} path="/forgot-password" noindex />
      <Shell testId="forgot-page" title={t("auth.forgotTitle")} lead={t("auth.forgotLead")}>
        {sent ? (
          <p data-testid="forgot-sent" className="text-sm text-accent">
            {t("auth.forgotSent")}
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
            <div>
              <Label htmlFor="forgot-email">{t("auth.email")}</Label>
              <Input id="forgot-email" data-testid="forgot-email" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)} className="mt-2" />
            </div>
            <Button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
              {busy ? t("common.loading") : t("common.submit")}
            </Button>
            <Link to={`/${lang}/login`} className="block text-sm text-muted-foreground hover:text-accent">
              {t("cta.login")}
            </Link>
          </form>
        )}
      </Shell>
    </>
  );
}

export function ResetPassword() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      setDone(true);
    } catch (err) {
      setError(apiError(err, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title={t("auth.resetTitle")} description={t("auth.resetLead")} path="/reset-password" noindex />
      <Shell testId="reset-page" title={t("auth.resetTitle")} lead={t("auth.resetLead")}>
        {done ? (
          <div>
            <p data-testid="reset-done" className="text-sm text-accent">
              {t("auth.resetDone")}
            </p>
            <Button asChild className="mt-6 w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to={`/${lang}/login`}>{t("cta.login")}</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div>
              <Label htmlFor="reset-password">{t("auth.password")}</Label>
              <Input id="reset-password" data-testid="reset-password" type="password" minLength={8} required value={password}
                     onChange={(e) => setPassword(e.target.value)} className="mt-2" />
              <p className="mt-1 text-xs text-muted-foreground">{t("auth.passwordRule")}</p>
            </div>
            {error && (
              <p data-testid="reset-error" role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy} data-testid="reset-submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        )}
      </Shell>
    </>
  );
}

export function VerifyEmail() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const [state, setState] = useState("pending");
  const { refresh } = useAuth();

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("failed");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(() => {
        setState("ok");
        refresh();
      })
      .catch(() => setState("failed"));
  }, [params, refresh]);

  return (
    <>
      <Seo title={t("auth.verifyTitle")} description={t("auth.verifyTitle")} path="/verify-email" noindex />
      <Shell testId="verify-page" title={t("auth.verifyTitle")} lead="">
        <p data-testid="verify-state" className={`text-sm ${state === "failed" ? "text-destructive" : "text-accent"}`}>
          {state === "pending" ? t("auth.verifying") : state === "ok" ? t("auth.verified") : t("auth.verifyFailed")}
        </p>
        <Button asChild className="mt-6 w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
          <Link to={`/${lang}/login`}>{t("cta.login")}</Link>
        </Button>
      </Shell>
    </>
  );
}
